import React, { useRef, useEffect, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import './BackgroundBlochSphere.css';

function Arrow({ direction, opacity, arrowLength, arrowRadius, coneRadius, coneHeight }) {
  const group = useRef();

  useEffect(() => {
    const dir = direction.clone().normalize();
    const axis = new THREE.Vector3(0, 1, 0);
    const quaternion = new THREE.Quaternion().setFromUnitVectors(axis, dir);
    if (group.current) {
      group.current.setRotationFromQuaternion(quaternion);
    }
  }, [direction]);

  return (
    <group ref={group}>
      <mesh position={[0, arrowLength / 2, 0]}>
        <cylinderGeometry args={[arrowRadius, arrowRadius, arrowLength, 8]} />
        <meshStandardMaterial color="#e94560" transparent opacity={opacity} depthWrite={false} />
      </mesh>
      <mesh position={[0, arrowLength, 0]}>
        <coneGeometry args={[coneRadius, coneHeight, 8]} />
        <meshStandardMaterial color="#e94560" transparent opacity={opacity} depthWrite={false} />
      </mesh>
    </group>
  );
}

function SpinningSphere({ setTooltip }) {
  const groupRef = useRef();
  const [hovered, setHovered] = useState(false);
  const [currentDirection, setCurrentDirection] = useState(new THREE.Vector3(0, 1, 0));
  const [trail, setTrail] = useState([]);

  const isMobile = window.innerWidth < 768;

  const sphereRadius = isMobile ? 2 : 2.5;
  const arrowRadius = isMobile ? 0.012 : 0.02;
  const arrowLength = isMobile ? 1.8 : 2;
  const coneHeight = isMobile ? 0.12 : 0.2;
  const coneRadius = isMobile ? 0.04 : 0.06;
  const labelFontSize = isMobile ? 0.3 : 0.3;
  const labelOffset = isMobile ? 2.5 : 3.5;

  useFrame(() => {
    if (!hovered && groupRef.current) {
      groupRef.current.rotation.y += 0.003;
    }

    const now = Date.now();
    const duration = 1000;
    const maxFade = 0.3;

    setTrail((prev) =>
      prev
        .map((t) => {
          const age = now - t.created;
          const fade = Math.max(0, 1 - age / duration);
          const easedOpacity = maxFade * Math.pow(fade, 2.5);
          return { ...t, opacity: easedOpacity };
        })
        .filter((t) => t.opacity > 0.01)
    );
  });

  useEffect(() => {
    if (hovered) return;

    const interval = setInterval(() => {
      const newDir = new THREE.Vector3(
        Math.random() - 0.5,
        Math.random() - 0.5,
        Math.random() - 0.5
      ).normalize();

      setTrail((prev) => [...prev.slice(-1), { direction: currentDirection.clone(), created: Date.now() }]);
      setCurrentDirection(newDir);
    }, 500);

    return () => clearInterval(interval);
  }, [hovered, currentDirection]);

  const handlePointerMove = (e) => {
    setHovered(true);
    const dir = currentDirection.clone().normalize();
    const theta = Math.acos(dir.y);
    const phi = Math.atan2(dir.z, dir.x);
    setTooltip({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      theta: theta.toFixed(2),
      phi: phi.toFixed(2),
    });
  };

  const handlePointerOut = () => {
    setHovered(false);
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  return (
    <group ref={groupRef} onPointerMove={handlePointerMove} onPointerOut={handlePointerOut}>
      <mesh>
        <sphereGeometry args={[sphereRadius, 32, 32]} />
        <meshBasicMaterial
          color="#cbd5e1"
          wireframe
          transparent
          opacity={0.1}
          toneMapped={false}
        />
      </mesh>

      <Arrow
        direction={currentDirection}
        opacity={1}
        arrowLength={arrowLength}
        arrowRadius={arrowRadius}
        coneRadius={coneRadius}
        coneHeight={coneHeight}
      />
      {trail.map((t) => (
        <Arrow
          key={t.created}
          direction={t.direction}
          opacity={t.opacity}
          arrowLength={arrowLength}
          arrowRadius={arrowRadius}
          coneRadius={coneRadius}
          coneHeight={coneHeight}
        />
      ))}

      <Text position={[0, labelOffset, 0]} fontSize={labelFontSize} color="white" anchorX="center" anchorY="middle">
        |0⟩
      </Text>
      <Text position={[0, -labelOffset, 0]} fontSize={labelFontSize} color="white" anchorX="center" anchorY="middle">
        |1⟩
      </Text>
    </group>
  );
}

export default function BackgroundBlochSphere() {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, theta: 0, phi: 0 });

  return (
    <>
      <div className="background-bloch-container">
        <Canvas gl={{ toneMapping: THREE.NoToneMapping }}>
          <ambientLight intensity={0.6} />
          <pointLight position={[5, 5, 5]} />
          <SpinningSphere setTooltip={setTooltip} />
        </Canvas>
      </div>

      <div
        className={`coordinate-tooltip ${tooltip.visible ? 'visible' : ''}`}
        style={{ top: tooltip.y + 10, left: tooltip.x + 10 }}
      >
        θ: {tooltip.theta} rad<br />
        φ: {tooltip.phi} rad
      </div>
    </>
  );
}
